import {
  checkVital,
  SCHNELLTEST_ERGEBNISSE,
  VITAL_TYPES,
  type CaseForPharmacy,
  type VitalInput,
  type VitalType,
} from "@omony/domain";
import { Badge, Banner, Button, Segmented, Select, TextField, useToast } from "@omony/ui";
import { Save } from "lucide-react";
import { useState } from "react";
import { useIntl } from "react-intl";
import { ErrorBanner } from "../../shared/auth/screens.js";
import { SEP } from "../../shared/format.js";
import { vitalText } from "../../shared/vitals.js";
import { useCaseAction } from "../data.js";
import { useT } from "./common.js";
import styles from "./flow.module.css";

type Raw = {
  sys: string;
  dia: string;
  puls: string;
  temp: string;
  spo2: string;
  bz: string;
  bzUnit: "mg/dl" | "mmol/l";
  test: string;
  ergebnis: string;
};
const EMPTY: Raw = {
  sys: "",
  dia: "",
  puls: "",
  temp: "",
  spo2: "",
  bz: "",
  bzUnit: "mg/dl",
  test: "",
  ergebnis: "",
};

const num = (s: string) => (s.trim() ? Number(s.replace(",", ".")) : NaN);
const int = (s: string) => Math.round(num(s));

/** Eingaben → strukturierte Werte (nur ausgefüllte Karten) */
export function toInputs(r: Raw): Array<{ type: VitalType; input: VitalInput }> {
  const out: Array<{ type: VitalType; input: VitalInput }> = [];
  if (r.sys.trim() || r.dia.trim())
    out.push({ type: "rr", input: { type: "rr", sys: int(r.sys), dia: int(r.dia) } });
  if (r.puls.trim()) out.push({ type: "puls", input: { type: "puls", value: int(r.puls) } });
  if (r.temp.trim()) out.push({ type: "temp", input: { type: "temp", value: num(r.temp) } });
  if (r.spo2.trim()) out.push({ type: "spo2", input: { type: "spo2", value: int(r.spo2) } });
  if (r.bz.trim())
    out.push({ type: "blutzucker", input: { type: "blutzucker", value: num(r.bz), unit: r.bzUnit } });
  if (r.test.trim() || r.ergebnis)
    out.push({
      type: "schnelltest",
      input: { type: "schnelltest", test: r.test.trim(), ergebnis: (r.ergebnis || "ungueltig") as "positiv" },
    });
  return out;
}

/** Vitalwerte (A20) mit Plausibilitätsprüfung (R12): Fehler am Feld, Warnbereich als Hinweis */
export function VitalsPanel({ c }: { c: CaseForPharmacy }) {
  const t = useT();
  const intl = useIntl();
  const toast = useToast();
  const [raw, setRaw] = useState<Raw>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<VitalType, string>>>({});
  const save = useCaseAction<{ items: VitalInput[]; requestId?: string }>(c.id, "vitals");
  const open = c.vitalRequests.filter((r) => r.status === "offen");
  const requested = new Set(open.flatMap((r) => r.items));
  const set = (patch: Partial<Raw>) => {
    setRaw((x) => ({ ...x, ...patch }));
    setErrors({});
  };

  const preview = new Map(
    toInputs(raw).map(({ type, input }) => {
      const ch = checkVital(input);
      return [type, ch.ok ? ch.warnings : null] as const;
    }),
  );

  const submit = () => {
    const items = toInputs(raw);
    if (items.length === 0) return;
    const errs: Partial<Record<VitalType, string>> = {};
    for (const { type, input } of items) {
      if (type === "schnelltest" && (!raw.test.trim() || !raw.ergebnis)) {
        errs.schnelltest = t("console.vitals.error.test");
        continue;
      }
      const ch = checkVital(input);
      if (!ch.ok) errs[type] = t(`console.vitals.error.${ch.error}`);
    }
    setErrors(errs);
    if (Object.keys(errs).length) return;
    save.mutate(
      { items: items.map((i) => i.input), ...(open[0] ? { requestId: open[0].id } : {}) },
      {
        onSuccess: () => {
          setRaw(EMPTY);
          toast(t("console.vitals.saved"));
        },
      },
    );
  };

  const card = (type: VitalType, body: React.ReactNode) => {
    const warn = preview.get(type);
    return (
      <div
        key={type}
        className={`${styles.vitalCard} ${requested.has(type) ? styles.vitalCardRequested : ""}`}
      >
        <div className={styles.caseMeta}>
          <strong>{t(`vital.${type}`)}</strong>
          {requested.has(type) ? <Badge tone="warning">{t("console.vitals.requested")}</Badge> : null}
        </div>
        {body}
        {errors[type] ? (
          <p className={styles.vitalHint} role="alert">
            {errors[type]}
          </p>
        ) : warn && warn.length ? (
          <p className={styles.vitalHint}>{warn.map((w) => t(`vital.warning.${w}`)).join(" · ")}</p>
        ) : null}
      </div>
    );
  };

  const field = (key: keyof Raw, label: string, decimal = false) => (
    <TextField
      label={label}
      value={raw[key]}
      onChange={(v) => set({ [key]: v.replace(/[^\d.,]/g, "") } as Partial<Raw>)}
      inputMode={decimal ? "decimal" : "numeric"}
      autoComplete="off"
      code
    />
  );

  return (
    <section className={styles.section} aria-labelledby="vitals-title">
      <h2 id="vitals-title" className={styles.sectionTitle}>
        {t("console.vitals.title")}
      </h2>
      {open.length ? (
        <Banner tone="warning" live>
          {t("console.vitals.openRequest", { items: [...requested].map((v) => t(`vital.${v}`)).join(", ") })}
          {open[0]?.note ? SEP + open[0].note : null}
        </Banner>
      ) : null}
      <div className={styles.vitalGrid}>
        {VITAL_TYPES.map((type) => {
          switch (type) {
            case "rr":
              return card(
                type,
                <div className={styles.vitalRow}>
                  {field("sys", t("console.vitals.sys"))}
                  {field("dia", t("console.vitals.dia"))}
                </div>,
              );
            case "puls":
              return card(type, field("puls", t("console.vitals.unit.puls")));
            case "temp":
              return card(type, field("temp", t("console.vitals.unit.temp"), true));
            case "spo2":
              return card(type, field("spo2", t("console.vitals.unit.spo2")));
            case "blutzucker":
              return card(
                type,
                <>
                  {field("bz", t("console.vitals.value"), true)}
                  <Segmented
                    label={t("console.vitals.unit")}
                    value={raw.bzUnit}
                    onChange={(v) => set({ bzUnit: v as Raw["bzUnit"] })}
                    items={[
                      { id: "mg/dl", label: t("console.vitals.unit.mgdl") },
                      { id: "mmol/l", label: t("console.vitals.unit.mmol") },
                    ]}
                  />
                </>,
              );
            case "schnelltest":
              return card(
                type,
                <>
                  <TextField
                    label={t("console.vitals.testName")}
                    value={raw.test}
                    onChange={(v) => set({ test: v })}
                  />
                  <Select
                    label={t("console.vitals.testResult")}
                    value={raw.ergebnis || null}
                    placeholder={t("patient.bogen.choose")}
                    onChange={(v) => set({ ergebnis: v })}
                    options={SCHNELLTEST_ERGEBNISSE.map((e) => ({
                      id: e,
                      label: t(`vital.schnelltest.${e}`),
                    }))}
                  />
                </>,
              );
          }
        })}
      </div>
      <div className={styles.actions}>
        <span className={styles.spacer} />
        <Button icon={<Save />} onPress={submit} isDisabled={save.isPending || toInputs(raw).length === 0}>
          {t("console.vitals.save")}
        </Button>
      </div>
      <ErrorBanner error={save.error} />
      {c.vitals.length ? (
        <ul className={styles.vitalList} aria-label={t("console.vitals.recorded")}>
          {c.vitals.map((v) => (
            <li key={v.id}>
              <span className={styles.mono}>{intl.formatTime(v.recordedAt)}</span>
              {SEP}
              {t("console.vitals.entry", { type: t(`vital.${v.value.type}`), value: vitalText(t, v.value) })}
              {v.plausibility === "warnung" ? (
                <Badge tone="warning">{t("console.vitals.warning")}</Badge>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
